const page = (id, label) => ({ id, label, permission: `page_${id}` });

export const PAGE_ACCESS_MARKER = 'granular_page_access';

export const ACCESS_MODULES = [
  {
    id: 'team_presence',
    label: 'Equipe Online',
    icon: 'i-lucide-users',
    permission: 'team_presence_manage',
    description: 'Permite acompanhar a presença e a atividade da equipe.',
    pages: [page('team_presence', 'Equipe Online')],
  },
  {
    id: 'inbox',
    label: 'Caixa de entrada',
    icon: 'i-lucide-inbox',
    permission: 'inbox_manage',
    description: 'Permite acessar a caixa de entrada de notificações.',
    pages: [page('inbox', 'Caixa de entrada')],
  },
  {
    id: 'conversations',
    label: 'Conversas',
    icon: 'i-lucide-message-circle',
    permission: null,
    description: 'Controla quais conversas o perfil pode visualizar e atender.',
    pages: [
      page('conversations_all', 'Todas'),
      page('conversations_mentions', 'Menções'),
      page('conversations_participating', 'Participando'),
      page('conversations_unattended', 'Novas'),
      page('conversations_folders', 'Pastas'),
      page('conversations_teams', 'Equipes'),
      page('conversations_channels', 'Canais'),
      page('conversations_labels', 'Etiquetas'),
    ],
  },
  {
    id: 'captain',
    label: 'Jarvis',
    icon: 'i-woot-captain',
    permission: 'captain_manage',
    description: 'Permite acessar e administrar os recursos de IA do Jarvis.',
    pages: [
      page('captain_faqs', 'FAQs'),
      page('captain_documents', 'Documentos'),
      page('captain_scenarios', 'Cenários'),
      page('captain_playground', 'Playground'),
      page('captain_inboxes', 'Caixas de entrada'),
      page('captain_tools', 'Ferramentas'),
      page('captain_settings', 'Configurações'),
    ],
  },
  {
    id: 'contacts',
    label: 'Contatos',
    icon: 'i-lucide-contact',
    permission: 'contact_manage',
    description: 'Permite consultar e gerenciar os contatos visíveis na conta.',
    pages: [
      page('contacts_all', 'Todos'),
      page('contacts_active', 'Ativos'),
      page('contacts_segments', 'Segmentos'),
      page('contacts_labels', 'Etiquetas'),
    ],
  },
  {
    id: 'companies',
    label: 'Empresas',
    icon: 'i-lucide-building-2',
    permission: 'company_manage',
    description: 'Permite consultar e administrar empresas.',
    pages: [page('companies_all', 'Todas as empresas')],
  },
  {
    id: 'campaigns',
    label: 'Campanhas',
    icon: 'i-lucide-megaphone',
    permission: 'campaign_manage',
    description: 'Permite criar e administrar campanhas.',
    pages: [
      page('campaigns_live_chat', 'Chat ao vivo'),
      page('campaigns_sms', 'SMS'),
      page('campaigns_whatsapp', 'WhatsApp'),
    ],
  },
  {
    id: 'search',
    label: 'Buscar conversas',
    icon: 'i-lucide-search',
    permission: 'search_manage',
    description: 'Permite pesquisar conversas, mensagens e contatos.',
    pages: [page('search', 'Buscar conversas')],
  },
  {
    id: 'canned_responses',
    label: 'Respostas prontas',
    icon: 'i-lucide-message-square-quote',
    permission: 'canned_response_manage',
    description: 'Permite consultar e administrar respostas prontas.',
    pages: [page('canned_responses', 'Respostas prontas')],
  },
  {
    id: 'portfolio',
    label: 'Carteirização',
    icon: 'i-lucide-folder-kanban',
    permission: 'portfolio_manage',
    description: 'Permite consultar e alterar a carteira de contatos.',
    pages: [page('portfolio', 'Carteirização')],
  },
  {
    id: 'integrations',
    label: 'Integrações',
    icon: 'i-lucide-plug-zap',
    permission: 'integration_manage',
    description: 'Permite configurar canais e integrações da conta.',
    pages: [
      page('integrations', 'Integrações'),
      {
        id: 'integrations_keys',
        label: 'Chaves de instância',
        permission: 'whatsapp_keys_manage',
      },
      {
        id: 'integrations_inboxes',
        label: 'Inboxes',
        permission: 'managed_inboxes_manage',
      },
    ],
  },
  {
    id: 'settings',
    label: 'Configurações',
    icon: 'i-lucide-settings',
    permission: 'settings_manage',
    description: 'Permite administrar usuários e configurações da equipe.',
    pages: [
      page('settings_agents', 'Usuários'),
      page('settings_teams', 'Equipes'),
      page('settings_canned_responses', 'Respostas prontas'),
      page('settings_team_numbers', 'Números da equipe'),
    ],
  },
];

export const INTEGRATION_MANAGEMENT_PERMISSIONS = [
  'whatsapp_keys_manage',
  'managed_inboxes_manage',
];

export const PAGE_PERMISSIONS = ACCESS_MODULES.flatMap(
  module => module.pages
).map(item => item.permission);

export const ADDITIONAL_PERMISSIONS = ACCESS_MODULES.filter(
  module => module.permission
).map(module => ({
  id: module.permission,
  label: module.label,
  description: module.description,
  icon: module.icon,
}));

export const CONVERSATION_ACTIONS = [
  { id: 'conversation_view', label: 'Visualizar' },
  { id: 'conversation_page_access', label: 'Acessar página' },
  { id: 'conversation_create', label: 'Iniciar conversa' },
  { id: 'conversation_reply', label: 'Responder' },
  { id: 'conversation_claim', label: 'Assumir atendimento' },
  { id: 'conversation_transfer', label: 'Transferir' },
  { id: 'conversation_resolve', label: 'Concluir' },
  { id: 'conversation_reopen', label: 'Reabrir' },
];

export const CONVERSATION_ACTION_PERMISSION_IDS = CONVERSATION_ACTIONS.map(
  action => action.id
);

export const CONVERSATION_SCOPE_PERMISSIONS = [
  'conversation_manage',
  'conversation_unassigned_manage',
  'conversation_participating_manage',
];

const PROFILE_EDITOR_PERMISSION_IDS = [
  ...CONVERSATION_SCOPE_PERMISSIONS,
  ...CONVERSATION_ACTION_PERMISSION_IDS,
  ...ADDITIONAL_PERMISSIONS.map(permission => permission.id),
  PAGE_ACCESS_MARKER,
  ...PAGE_PERMISSIONS,
];

export const extractConversationActions = permissions => {
  const selected = CONVERSATION_ACTION_PERMISSION_IDS.filter(permission =>
    permissions.includes(permission)
  );
  if (selected.length || permissions.includes(PAGE_ACCESS_MARKER))
    return selected;

  const hasLegacyConversationAccess = CONVERSATION_SCOPE_PERMISSIONS.some(
    permission => permissions.includes(permission)
  );
  return hasLegacyConversationAccess
    ? [...CONVERSATION_ACTION_PERMISSION_IDS]
    : [];
};

export const conversationScopeForActions = (
  conversationActions,
  preservedConversationScope = ''
) => {
  if (!conversationActions.length) return '';
  if (preservedConversationScope === 'conversation_manage') {
    return preservedConversationScope;
  }
  if (conversationActions.includes('conversation_claim')) {
    return 'conversation_unassigned_manage';
  }

  const needsConversationVisibility = conversationActions.some(permission =>
    [
      'conversation_view',
      'conversation_reply',
      'conversation_transfer',
      'conversation_resolve',
      'conversation_reopen',
    ].includes(permission)
  );
  return needsConversationVisibility ? 'conversation_participating_manage' : '';
};

export const buildCustomRolePermissions = ({
  conversationActions = [],
  selectedPermissions = [],
  pagePermissions = [],
  preservedPermissions = [],
  preservedConversationScope = '',
  preservedConversationScopes = [],
}) => {
  const conversationScope = conversationScopeForActions(
    conversationActions,
    preservedConversationScope
  );
  let conversationScopes = conversationScope ? [conversationScope] : [];
  if (conversationActions.length && preservedConversationScopes.length) {
    conversationScopes = preservedConversationScopes.filter(scope =>
      CONVERSATION_SCOPE_PERMISSIONS.includes(scope)
    );
  }
  return [
    ...conversationScopes,
    ...conversationActions,
    ...selectedPermissions,
    ...preservedPermissions,
    PAGE_ACCESS_MARKER,
    ...pagePermissions,
  ];
};

export const extractAdditionalPermissions = permissions =>
  ADDITIONAL_PERMISSIONS.filter(permission =>
    permissions.includes(permission.id)
  ).map(permission => permission.id);

export const extractPreservedPermissions = permissions =>
  permissions.filter(
    permission => !PROFILE_EDITOR_PERMISSION_IDS.includes(permission)
  );

export const extractPagePermissions = permissions => {
  if (!permissions.includes(PAGE_ACCESS_MARKER)) {
    return ACCESS_MODULES.filter(module => {
      if (module.id === 'conversations') {
        return CONVERSATION_SCOPE_PERMISSIONS.some(permission =>
          permissions.includes(permission)
        );
      }
      return module.permission && permissions.includes(module.permission);
    }).flatMap(module =>
      module.pages
        .filter(
          item =>
            !INTEGRATION_MANAGEMENT_PERMISSIONS.includes(item.permission) ||
            permissions.includes(item.permission)
        )
        .map(item => item.permission)
    );
  }

  return PAGE_PERMISSIONS.filter(permission =>
    permissions.includes(permission)
  );
};

export const extractConversationScope = permissions =>
  CONVERSATION_SCOPE_PERMISSIONS.find(permission =>
    permissions.includes(permission)
  ) || '';
